import { Button, Flex } from "antd";
import { ArrowUUpLeftIcon, ArrowUUpRightIcon } from "@phosphor-icons/react";
import { useCallback } from "react";
import { useTranslate } from "@refinedev/core";
import { useAntTheme } from "hooks/useAntTheme/";
import { useTemplateEditorTemporal } from "../../store/useTemplateEditorStore";
import { useUndoRedoShortcuts } from "../../hooks/useUndoRedoShortcuts";
import { CopyPaste } from "../controls/CopyPaste";
import { ToolbarDivider } from "./ToolbarDivider";
import { ZoomControl } from "../ZoomControl";

export type EditorToolbarProps = {
  onCopy: () => void;
  onPaste: () => void;
  canCopy: boolean;
  canPaste: boolean;
  /** Fired after an undo or redo runs (e.g. to mark the template as updated). */
  onUndoRedo?: () => void;
};

export const EditorToolbar = ({
  onCopy,
  onPaste,
  canCopy,
  canPaste,
  onUndoRedo,
}: EditorToolbarProps) => {
  const { theme } = useAntTheme();
  const t = useTranslate();

  const { undo, redo, futureStates, pastStates } = useTemplateEditorTemporal();

  const handleUndoRedo = useCallback(
    (action: (steps?: number) => void) => {
      action();
      onUndoRedo?.();
    },
    [onUndoRedo]
  );

  useUndoRedoShortcuts(
    () => handleUndoRedo(undo),
    () => handleUndoRedo(redo),
    pastStates.length > 0,
    futureStates.length > 0
  );

  return (
    <Flex align="center">
      <Button
        type="text"
        onClick={() => handleUndoRedo(undo)}
        disabled={pastStates.length === 0}
        shape="round"
      >
        <ArrowUUpLeftIcon size={20} color={theme.colorIcon} />
        {t("buttons.undo")}
      </Button>
      <Button
        type="text"
        onClick={() => handleUndoRedo(redo)}
        disabled={futureStates.length === 0}
        shape="round"
      >
        <ArrowUUpRightIcon size={20} color={theme.colorIcon} />
        {t("buttons.redo")}
      </Button>
      <ToolbarDivider style={{ alignSelf: "center", marginInline: 8 }} />
      <CopyPaste
        onCopy={onCopy}
        onPaste={onPaste}
        canCopy={canCopy}
        canPaste={canPaste}
      />
      <ToolbarDivider style={{ alignSelf: "center", marginInline: 8 }} />
      <ZoomControl />
    </Flex>
  );
};
